import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

const MIN_DURATION_MS = 30 * 60 * 1000
const DEFAULT_DURATION_MS = 60 * 60 * 1000

export default class extends Controller {
  static targets = ["startInput", "endInput", "startTrigger", "endTrigger", "startLabel", "endLabel"]
  static values = { futureOnly: { type: Boolean, default: true } }

  connect() {
    this.startPicker = this.#createPicker(
      this.startInputTarget,
      this.startTriggerTarget,
      this.startLabelTarget,
      (date) => this.#onStartChange(date)
    )
    this.endPicker = this.#createPicker(
      this.endInputTarget,
      this.endTriggerTarget,
      this.endLabelTarget
    )

    this.#syncLabelFromInput(this.startPicker, this.startInputTarget, this.startLabelTarget)
    this.#syncLabelFromInput(this.endPicker, this.endInputTarget, this.endLabelTarget)

    const startDate = this.startPicker.selectedDates[0]
    if (startDate) this.#onStartChange(startDate)
  }

  disconnect() {
    this.startPicker?.destroy()
    this.endPicker?.destroy()
  }

  openStart(event) {
    event.preventDefault()
    this.startPicker.open()
  }

  openEnd(event) {
    event.preventDefault()
    this.endPicker.open()
  }

  #createPicker(input, trigger, label, onChange) {
    const picker = flatpickr(input, {
      enableTime: true,
      time_24hr: true,
      minuteIncrement: 15,
      dateFormat: "Y-m-d H:i",
      disableMobile: true,
      clickOpens: false,
      allowInput: false,
      minDate: this.futureOnlyValue ? "today" : undefined,
      appendTo: document.body,
      positionElement: trigger,
      onOpen: (_dates, _str, instance) => this.#syncCalendarTheme(instance.calendarContainer),
      onChange: (dates) => {
        const date = dates[0]
        if (date) {
          input.value = picker.formatDate(date, "Y-m-d H:i")
          this.#setLabel(label, date)
          onChange?.(date)
        } else {
          input.value = ""
          this.#clearLabel(label)
        }
      },
      onReady: (_dates, _str, instance) => this.#decorateCalendar(instance)
    })

    return picker
  }

  #onStartChange(startDate) {
    if (!startDate) return

    const minEnd = this.#minEndDate(startDate)
    this.endPicker.set("minDate", minEnd)

    const endDate = this.endPicker.selectedDates[0]
    if (this.#endTooEarly(startDate, endDate)) {
      this.#applyEndDate(this.#defaultEndDate(startDate))
    }
  }

  #minEndDate(startDate) {
    return new Date(startDate.getTime() + MIN_DURATION_MS)
  }

  #defaultEndDate(startDate) {
    return new Date(startDate.getTime() + DEFAULT_DURATION_MS)
  }

  #endTooEarly(startDate, endDate) {
    if (!endDate) return true
    return endDate.getTime() < startDate.getTime() + MIN_DURATION_MS
  }

  #applyEndDate(date) {
    this.endPicker.setDate(date, true)
    this.endInputTarget.value = this.endPicker.formatDate(date, "Y-m-d H:i")
    this.#setLabel(this.endLabelTarget, date)
  }

  #syncLabelFromInput(picker, input, label) {
    const date = this.#parseStoredValue(picker, input.value)
    if (!date || Number.isNaN(date.getTime())) return
    picker.setDate(date, false)
    input.value = picker.formatDate(date, "Y-m-d H:i")
    this.#setLabel(label, date)
  }

  #parseStoredValue(picker, raw) {
    const trimmed = (raw || "").trim()
    if (!trimmed) return null
    return (
      picker.parseDate(trimmed, "Y-m-d H:i") ||
      picker.parseDate(trimmed, "Y-m-d\\TH:i:S") ||
      new Date(trimmed)
    )
  }

  #setLabel(label, date) {
    label.textContent = this.#formatLabel(date)
    label.classList.add("date-time-picker__label--set")
    label.closest(".date-time-picker__trigger")?.classList.add("date-time-picker__trigger--set")
  }

  #clearLabel(label) {
    label.textContent = "Pick date & time"
    label.classList.remove("date-time-picker__label--set")
    label.closest(".date-time-picker__trigger")?.classList.remove("date-time-picker__trigger--set")
  }

  #formatLabel(date) {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(date)
  }

  #syncCalendarTheme(calendar) {
    const dark = document.documentElement.classList.contains("dark")
    calendar.classList.toggle("date-time-picker-calendar--dark", dark)
  }

  #decorateCalendar(instance) {
    const cal = instance.calendarContainer
    cal.classList.add("date-time-picker-calendar")
    this.#syncCalendarTheme(cal)

    const timeEl = cal.querySelector(".flatpickr-time")
    if (timeEl && !timeEl.closest(".date-time-picker__time-panel")) {
      const panel = document.createElement("div")
      panel.className = "date-time-picker__time-panel"
      const heading = document.createElement("p")
      heading.className = "date-time-picker__time-heading"
      heading.textContent = "Time"
      timeEl.parentNode.insertBefore(panel, timeEl)
      panel.appendChild(heading)
      panel.appendChild(timeEl)
    }

    if (cal.querySelector(".date-time-picker__footer")) return

    const footer = document.createElement("div")
    footer.className = "date-time-picker__footer"

    const today = document.createElement("button")
    today.type = "button"
    today.className = "date-time-picker__today-btn"
    today.textContent = "Today"
    today.addEventListener("click", () => instance.setDate(new Date(), true))

    footer.appendChild(today)
    cal.appendChild(footer)
  }
}
